import { parse } from "graphql";
import { print } from "graphql/language/printer";
import { rawRequest, ClientError } from "graphql-request";
import { z } from "zod";
import { unset } from "../lib/unset";
import { get } from "../lib/get";
import { set } from "../lib/set";

const SLEEP_AFTER_PAGE_MS = 1000;
const RETRY_AFTER_COEFFICIENT = 3; // Sleep triple the retry time to compensate for all the other requests that might be happening at the same time

const sleep = async (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export type ContentfulRequestContext = {
  spaceId: string;
  apiKey: string;
  environment: string;
  preview?: boolean;
  contentTimeLimit?: number;
  carouselEntryLimit?: number;
  alwaysIncludedCarouselEntries?: string[];
};

export function configFromEnvironment(): ContentfulRequestContext {
  const spaceId = process.env.CONTENTFUL_SPACE_ID;
  if (spaceId === undefined) {
    console.error("no CONTENTFUL_SPACE_ID found in environment");
    process.exit(1);
  }

  const apiKey = process.env.CONTENTFUL_API_KEY;
  if (apiKey === undefined) {
    console.error("no CONTENTFUL_API_KEY found in environment");
    process.exit(1);
  }

  const environment = process.env.CONTENTFUL_ENVIRONMENT ?? "master";

  return { spaceId, apiKey, environment };
}

export const ContentfulGraphQLResponse = z.object({
  data: z.record(
    z.object({
      total: z.number().optional(),
      skip: z.number().optional(),
      items: z.array(z.unknown()),
    }),
  ),
});

export type ContentfulGraphQLResponse = z.infer<
  typeof ContentfulGraphQLResponse
>;

const removeDuplicateFragments = (query: string): string => {
  const ast = parse(query);

  const seen = new Set<string>();

  const newDefinitions = ast.definitions.filter((def) => {
    if (def.kind !== "FragmentDefinition") {
      return true;
    }

    const id = `${def.name.value}-${def.typeCondition.name.value}`;

    const haveSeen = seen.has(id);

    seen.add(id);

    return !haveSeen;
  });

  const newAst = {
    ...ast,
    definitions: newDefinitions,
  };

  return print(newAst);
};

export async function doContentfulRequest(
  query: string,
  variables?: Record<string, unknown>,
  ctx?: ContentfulRequestContext,
): Promise<ContentfulGraphQLResponse> {
  const config = ctx !== undefined ? ctx : configFromEnvironment();

  const url = `https://graphql.contentful.com/content/v1/spaces/${config.spaceId}/environments/${config.environment}`;

  const ITEM_LIMIT = variables?.limit ?? 50;

  const paginationRequestHelper = async (
    skip: number,
  ): Promise<ContentfulGraphQLResponse> => {
    try {
      const params = {
        ...variables,
        preview: ctx?.preview ?? false,
        limit: ITEM_LIMIT,
        skip,
      };

      const sansDuplicated = removeDuplicateFragments(query);

      const minified = sansDuplicated.replaceAll("  ", "");

      const result = await rawRequest(url, minified, params, {
        Authorization: `Bearer ${config.apiKey}`,
      });

      const parsed = ContentfulGraphQLResponse.parse(result);

      return parsed;
    } catch (ex) {
      if (!(ex instanceof ClientError)) {
        throw ex;
      }

      const codes = ex.response.errors?.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e) => (e.extensions?.contentful as any)?.code,
      );

      if (codes === undefined || codes.some((x) => x === undefined)) throw ex;

      // Error case: rate limit exceeded
      // https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/api-rate-limits
      const rateLimitCodes = codes.filter((c) => c === "RATE_LIMIT_EXCEEDED");

      if (rateLimitCodes.length > 0) {
        console.log("Rate limit exceeded, retrying after 1 second");

        const retryAfterSeconds =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ex.response.headers as any)["x-contentful-ratelimit-reset"] ?? 1;

        await sleep(retryAfterSeconds * RETRY_AFTER_COEFFICIENT * 1000);

        return paginationRequestHelper(skip);
      }

      // If there are references to draft entries, we have to remove them based on the error response
      // See https://github.com/contentful/contentful.js/issues/418
      const unresolvableLinkErrors = codes.filter(
        (c) => c === "UNRESOLVABLE_LINK",
      );

      if (unresolvableLinkErrors.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = { ...(ex.response.data as any) };

        const errorPaths = ex.response.errors?.map((e) => e.path ?? []);

        if (errorPaths === undefined) throw ex;

        for (const errorPath of errorPaths) {
          const errorParentPath = errorPath.slice(0, -1);
          const existing = get(data, errorParentPath);

          // We're dealing with an array of references
          if (Array.isArray(existing)) {
            const idxToRemove = errorPath[errorPath.length - 1];
            // Remove the invalid entry reference
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newArray = existing.slice(0, idxToRemove as any as number);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            set(ex.response.data as any, errorParentPath, newArray);
          } else {
            unset(ex.response.data, errorPath);
          }
        }

        const resp = ContentfulGraphQLResponse.parse({
          data,
        });

        return resp;
      }

      throw ex;
    }
  };

  const firstResult = await paginationRequestHelper(0);

  // We're assuming that the graphql query will return a single object with a single key, which is fine for now I think
  const key = Object.keys(firstResult.data)[0];
  const entry = firstResult.data[key];
  const { total, items } = entry;

  // The graphQL query might not have the `total` specified, in which case we just return the response as is
  if (!total) {
    return firstResult;
  }

  const itemsClone = [...items];

  while (itemsClone.length < total) {
    const paginatedResult = await paginationRequestHelper(itemsClone.length);
    const { items } = paginatedResult.data[key];
    itemsClone.push(...items);

    // We're constantly hitting the rate limits, so let's sleep after every page to ease the burden a little bit
    await sleep(SLEEP_AFTER_PAGE_MS);
  }

  return {
    data: {
      [key]: {
        total,
        items: itemsClone,
      },
    },
  };
}

type EntryWithSortDate = {
  sortDate?: string | null;
  sys: {
    firstPublishedAt?: string | null;
  };
};

export function sortByDate<T extends EntryWithSortDate>(entries: T[]): T[] {
  return entries.sort((a, b) => {
    const aDate = a.sortDate ?? a.sys.firstPublishedAt;
    const bDate = b.sortDate ?? b.sys.firstPublishedAt;

    if (!aDate) {
      return -1;
    }

    if (!bDate) {
      return 1;
    }

    const aTime = new Date(aDate).getTime();
    const bTime = new Date(bDate).getTime();

    if (bTime < aTime) {
      return -1;
    }

    return 1;
  });
}

export function sortEntries<T extends EntryWithSortDate>(entries: T[]): T[] {
  const bySortDate = sortByDate<T>(entries);

  // Preview entries have firstPublishedAt set to null, so we sort them to be first
  const previewEntriesFirst = bySortDate.sort((a) => {
    if (a.sys.firstPublishedAt || a.sortDate) {
      return 0;
    }

    return -1;
  });

  return previewEntriesFirst;
}

export function subDays(dirtyDate: Date | string, amount: number): Date {
  const date = new Date(dirtyDate);

  if (!amount) {
    return date;
  }

  date.setDate(date.getDate() - amount);

  return date;
}
